import { useEffect } from "react";
import { initMonitoring } from "@/lib/monitoring";
import { initReliabilityTelemetry } from "@/lib/reliability";
import { trackPageView } from "@/lib/analytics";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import { WalletProvider } from "@/hooks/use-wallet";
import { I18nProvider } from "@/lib/i18n";
import { Toaster } from "@/components/ui/sonner";
import appCss from "../styles.css?url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "MealToken24 — SFN • Sustainable Food & Eco Network" },
      {
        name: "description",
        content:
          "Freezing the price of food with a dual-RWA ecosystem on Solana. Powered by community, backed by real assets and guaranteed price stability.",
      },
      { name: "author", content: "SFN • Sustainable Food & Eco Network" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "MealToken24" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@Neutral_Frame" },
      { property: "og:title", content: "MealToken24 — SFN • Sustainable Food & Eco Network" },
      { name: "twitter:title", content: "MealToken24 — SFN • Sustainable Food & Eco Network" },
      { property: "og:description", content: "Freezing the price of food with a dual-RWA ecosystem on Solana. Powered by community, backed by real assets and guaranteed price stability." },
      { name: "twitter:description", content: "Freezing the price of food with a dual-RWA ecosystem on Solana. Powered by community, backed by real assets and guaranteed price stability." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/fe9fdff3-0b81-4f4f-9acf-0a2a7e8f6fb6/id-preview-d56840d3--213938a7-1003-4164-97c1-6e43e669307c.lovable.app-1785548664007.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/fe9fdff3-0b81-4f4f-9acf-0a2a7e8f6fb6/id-preview-d56840d3--213938a7-1003-4164-97c1-6e43e669307c.lovable.app-1785548664007.png" },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "32x32" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico", sizes: "64x64" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Urbanist:wght@400;500;600;700;800;900&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=JetBrains+Mono:wght@400;500;700&display=swap",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  // SSR renders lang="en"; a tiny inline script upgrades <html lang> before
  // hydration so it matches the user's persisted / URL-selected locale.
  // Keeps in sync with the I18nProvider (which also writes documentElement.lang on change).
  const bootScript = `(()=>{try{var s=['en','de','zh','ja','ko','ru','pt'];var u=new URL(location.href).searchParams.get('lang');var k=localStorage.getItem('sfn.lang');var c=(document.cookie.match(/(?:^|; )sfn_lang=([^;]+)/)||[])[1];var n=(navigator.language||'').slice(0,2).toLowerCase();var l=[u,k,c&&decodeURIComponent(c),n].find(function(x){return x&&s.indexOf(x)>-1;})||'en';document.documentElement.lang=l;}catch(e){}})();`;
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <script dangerouslySetInnerHTML={{ __html: bootScript }} />
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  // Production diagnostics: uncaught errors / rejections are reported through
  // the monitoring pipeline (source maps are emitted for the client bundle).
  useEffect(() => {
    initMonitoring();
    // Anonymous reliability telemetry (RPC failures, degraded proxy states).
    initReliabilityTelemetry();
    // Silent, anonymous page-view counter (internal admin metrics only).
    trackPageView();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <WalletProvider>
        {/* App-level locale context so any route (incl. code-split ones such as
            /vision) can call useI18n during SSR without crashing. Routes that
            mount their own I18nProvider still work — the inner one wins. */}
        <I18nProvider>
          <Outlet />
          <Toaster position="top-right" richColors closeButton />
        </I18nProvider>
      </WalletProvider>
    </QueryClientProvider>
  );
}
